import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

export const NeonButton = ({ onClick, children, variant = "primary", style = {}, disabled = false }) => {
  const palette = {
    primary: { c: "#22d3ee", bg: "rgba(34,211,238,0.08)" },
    accent:  { c: "#3b82f6", bg: "rgba(59,130,246,0.08)" },
    purple:  { c: "#a855f7", bg: "rgba(168,85,247,0.08)" },
    green:   { c: "#10b981", bg: "rgba(16,185,129,0.08)" },
    ghost:   { c: "#6b8aad", bg: "transparent" },
  };
  const { c, bg } = palette[variant] || palette.primary;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: bg,
        border: `1px solid ${c}`,
        color: c,
        padding: "10px 24px",
        borderRadius: "8px",
        fontFamily: "Rajdhani, sans-serif",
        fontSize: "15px",
        fontWeight: 700,
        letterSpacing: "1.5px",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s",
        opacity: disabled ? 0.45 : 1,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...style,
      }}
      onMouseEnter={e => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = `0 0 18px ${c}55, 0 0 35px ${c}22`;
          e.currentTarget.style.background = `${c}18`;
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.background = bg;
      }}
    >
      {children}
    </button>
  );
};

export const GlassCard = ({ children, style = {}, glow = false, animate = true }) => (
  <div
    style={{
      background: "rgba(13,27,46,0.75)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      border: "1px solid rgba(34,211,238,0.15)",
      borderRadius: "14px",
      padding: "24px",
      animation: animate ? "slide-in-up 0.4s ease forwards" : "none",
      ...(glow ? { boxShadow: "0 0 20px rgba(34,211,238,0.15), 0 0 60px rgba(34,211,238,0.05)" } : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

export const Label = ({ children, required }) => (
  <label style={{
    display: "block",
    marginBottom: 7,
    color: "#94b8d4",
    fontSize: 12,
    fontFamily: "Exo 2, sans-serif",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    fontWeight: 500,
  }}>
    {children}
    {required && <span style={{ color: "#22d3ee", marginLeft: 4 }}>*</span>}
  </label>
);

const inputBaseStyle = {
  width: "100%",
  padding: "11px 14px",
  background: "rgba(6,13,26,0.85)",
  border: "1px solid rgba(34,211,238,0.18)",
  borderRadius: 8,
  color: "#e2f0ff",
  fontSize: 14,
  fontFamily: "Exo 2, sans-serif",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

export const InputField = ({ label, value, onChange, type = "text", placeholder, required, style = {}, readOnly = false }) => (
  <div style={{ marginBottom: 16, ...style }}>
    {label && <Label required={required}>{label}</Label>}
    <input
      type={type}
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{ ...inputBaseStyle, cursor: readOnly ? "default" : "text", color: readOnly ? "#6b8aad" : "#e2f0ff" }}
      onFocus={e => {
        e.target.style.borderColor = "#22d3ee";
        e.target.style.boxShadow = "0 0 10px rgba(34,211,238,0.2)";
      }}
      onBlur={e => {
        e.target.style.borderColor = "rgba(34,211,238,0.18)";
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
);

export const SelectField = ({ label, value, onChange, options, required, disabled = false, hint }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <Label required={required}>{label}</Label>}
    <div style={{ position: "relative" }}>
      <select
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
        disabled={disabled}
        style={{
          ...inputBaseStyle,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          appearance: "none",
          paddingRight: 36,
        }}
        onFocus={e => {
          e.target.style.borderColor = "#22d3ee";
          e.target.style.boxShadow = "0 0 10px rgba(34,211,238,0.2)";
        }}
        onBlur={e => {
          e.target.style.borderColor = "rgba(34,211,238,0.18)";
          e.target.style.boxShadow = "none";
        }}
      >
        <option value="">— Select —</option>
        {options.map(o => (
          typeof o === "object"
            ? <option key={o.value} value={o.value}>{o.label}</option>
            : <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: "#22d3ee", pointerEvents: "none", fontSize: 12 }}>▼</span>
    </div>
    {hint && <p style={{ margin: "5px 0 0", fontSize: 11, color: "#6b8aad", fontFamily: "Exo 2" }}>{hint}</p>}
  </div>
);

export const CheckGroup = ({ label, items, selected, onToggle }) => (
  <div style={{ marginBottom: 20 }}>
    <Label>{label}</Label>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map(item => {
        const active = selected.includes(item);
        return (
          <button
            key={item}
            onClick={() => onToggle(item)}
            style={{
              padding: "7px 14px",
              borderRadius: 20,
              background: active ? "rgba(34,211,238,0.15)" : "rgba(6,13,26,0.8)",
              border: `1px solid ${active ? "#22d3ee" : "rgba(34,211,238,0.15)"}`,
              color: active ? "#22d3ee" : "#6b8aad",
              fontSize: 13,
              fontFamily: "Exo 2, sans-serif",
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: active ? "0 0 8px rgba(34,211,238,0.2)" : "none",
            }}
          >
            {active && "✓ "}{item}
          </button>
        );
      })}
    </div>
  </div>
);

export const SectionHeader = ({ title, icon, subtitle }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: subtitle ? 4 : 18 }}>
    <span style={{ fontSize: 22 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{
          fontFamily: "Rajdhani, sans-serif",
          fontSize: 19,
          fontWeight: 700,
          color: "#e2f0ff",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          margin: 0,
        }}>
          {title}
        </h3>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,rgba(34,211,238,0.35),transparent)" }} />
      </div>
      {subtitle && <p style={{ margin: "2px 0 14px", fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2" }}>{subtitle}</p>}
    </div>
  </div>
);

// Progress bar with animated fill
export const ProgressBar = ({ value, color = "#22d3ee", height = 4, label, showPercent = false }) => (
  <div style={{ width: "100%" }}>
    {(label || showPercent) && (
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        {label && <span style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2" }}>{label}</span>}
        {showPercent && <span style={{ fontSize: 12, color, fontFamily: "Rajdhani", fontWeight: 700 }}>{value}%</span>}
      </div>
    )}
    <div style={{ height, background: "rgba(34,211,238,0.08)", borderRadius: height, overflow: "hidden" }}>
      <div style={{
        height: "100%",
        width: `${value}%`,
        background: `linear-gradient(90deg, ${color}, ${color}99)`,
        borderRadius: height,
        transition: "width 1s ease",
        boxShadow: `0 0 8px ${color}44`,
      }} />
    </div>
  </div>
);

// Score badge chip
export const ScoreBadge = ({ score, size = "md" }) => {
  const color = score >= 80 ? "#10b981" : score >= 60 ? "#22d3ee" : score >= 40 ? "#f59e0b" : "#ef4444";
  const sizes = { sm: { font: 14, pad: "3px 8px" }, md: { font: 18, pad: "5px 12px" }, lg: { font: 28, pad: "8px 18px" } };
  const s = sizes[size];
  return (
    <span style={{
      fontFamily: "Rajdhani, sans-serif",
      fontSize: s.font,
      fontWeight: 700,
      color,
      background: `${color}18`,
      border: `1px solid ${color}55`,
      borderRadius: 6,
      padding: s.pad,
      display: "inline-block",
    }}>
      {score}%
    </span>
  );
};

// Info row
export const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
    <span style={{ fontSize: 14, minWidth: 20 }}>{icon}</span>
    <span style={{ fontSize: 12, color: "#6b8aad", fontFamily: "Exo 2", minWidth: 100 }}>{label}:</span>
    <span style={{ fontSize: 13, color: "#e2f0ff", fontFamily: "Exo 2", flex: 1 }}>{value || "—"}</span>
  </div>
);